// Valve Texture Format (VTF) decoder — Source engine textures.
//
// The map-server serves the raw `.vtf` bytes (resolved from a material's
// `$basetexture`); the browser can't decode VTF natively, so we decode the
// top mip here. Supports the common Source formats: DXT1/DXT3/DXT5 (BC1/2/3)
// plus the uncompressed 8888/888 variants and 8-bit luminance/alpha.

import type { DecodedImage } from "./textures";

/** True if the buffer starts with the VTF signature (`VTF\0`). */
export function isVtf(b: Uint8Array): boolean {
  return b.length >= 4 && b[0] === 0x56 && b[1] === 0x54 && b[2] === 0x46 && b[3] === 0x00;
}

const FMT = {
  RGBA8888: 0x00,
  ABGR8888: 0x01,
  RGB888: 0x02,
  BGR888: 0x03,
  RGB565: 0x04,
  I8: 0x05,
  IA88: 0x06,
  P8: 0x07,
  A8: 0x08,
  ARGB8888: 0x0b,
  BGRA8888: 0x0c,
  DXT1: 0x0d,
  DXT3: 0x0e,
  DXT5: 0x0f,
  BGRX8888: 0x10,
  BGR565: 0x11,
  BGRX5551: 0x12,
  BGRA4444: 0x13,
  DXT1_ONEBITALPHA: 0x14,
  BGRA5551: 0x15,
  UV88: 0x16,
  UVLX8888: 0x23,
};

export function decodeVtf(b: Uint8Array): DecodedImage | null {
  if (!isVtf(b)) return null;
  const dv = new DataView(b.buffer, b.byteOffset, b.byteLength);
  const major = dv.getUint32(4, true);
  const minor = dv.getUint32(8, true);
  if (major !== 7) return null; // only v7.x (Source) supported

  const headerSize = dv.getUint32(12, true);
  const width = dv.getUint16(16, true);
  const height = dv.getUint16(18, true);
  const flags = dv.getUint32(20, true);
  const frames = dv.getUint16(24, true) || 1;
  const highResFormat = dv.getUint32(52, true);
  let mipCount = dv.getUint8(56);
  if (mipCount === 0) mipCount = 1;
  const lowResFormat = dv.getUint32(57, true);
  const lowResW = dv.getUint8(61);
  const lowResH = dv.getUint8(62);
  const depth = minor >= 2 ? dv.getUint16(63, true) || 1 : 1;
  const faces = flags & 0x4000 ? 6 : 1; // TEXTUREFLAGS_ENVMAP

  if (width === 0 || height === 0) return null;

  // Resource dictionary (v7.3+): a fixed 80-byte header is followed by
  // `numResources` 8-byte entries {ID[3], flags, data}. `data` is an absolute
  // file offset for the legacy low-res (0x01) / image (0x30) resources.
  let highResOffset = -1;
  let lowResOffset = -1;
  let imageStart = headerSize;
  if (minor >= 3 && b.length >= 84) {
    const numRes = dv.getUint32(68, true);
    let rp = 80;
    for (let i = 0; i < numRes && rp + 8 <= b.length; i++) {
      const id = dv.getUint32(rp, true) & 0x00ffffff;
      const data = dv.getUint32(rp + 4, true);
      if (id === 0x01) lowResOffset = data;
      else if (id === 0x30) highResOffset = data;
      rp += 8;
    }
    imageStart = rp;
  }

  let highResStart;
  if (highResOffset >= 0) {
    highResStart = highResOffset;
  } else {
    let s = imageStart;
    if (lowResFormat !== 0xffffffff && lowResW > 0 && lowResH > 0) {
      s += mipSize(lowResFormat, lowResW, lowResH);
    }
    highResStart = s;
  }
  void lowResOffset;

  // Mips are stored smallest-first, so mip 0 (full size) is the last block.
  let total = 0;
  let mip0Size = 0;
  for (let i = 0; i < mipCount; i++) {
    const mw = Math.max(1, width >> i);
    const mh = Math.max(1, height >> i);
    const sz = mipSize(highResFormat, mw, mh) * frames * faces * depth;
    total += sz;
    if (i === 0) mip0Size = sz;
  }
  const mip0Offset = highResStart + total - mip0Size;
  if (mip0Offset < 0 || mip0Offset + mip0Size > b.length) return null;

  const data = decodeImage(highResFormat, b, mip0Offset, width, height);
  if (!data) return null;
  return { width, height, data };
}

function blockBytes(fmt: number): number {
  switch (fmt) {
    case FMT.DXT1:
    case FMT.DXT1_ONEBITALPHA:
      return 8;
    case FMT.DXT3:
    case FMT.DXT5:
      return 16;
    default:
      return 0; // not block-compressed
  }
}

function bytesPerPixel(fmt: number): number {
  switch (fmt) {
    case FMT.RGBA8888:
    case FMT.ABGR8888:
    case FMT.ARGB8888:
    case FMT.BGRA8888:
    case FMT.BGRX8888:
    case FMT.UVLX8888:
      return 4;
    case FMT.RGB888:
    case FMT.BGR888:
      return 3;
    case FMT.RGB565:
    case FMT.IA88:
    case FMT.BGR565:
    case FMT.BGRX5551:
    case FMT.BGRA4444:
    case FMT.BGRA5551:
    case FMT.UV88:
      return 2;
    case FMT.I8:
    case FMT.P8:
    case FMT.A8:
      return 1;
    default:
      return 4;
  }
}

function mipSize(fmt: number, w: number, h: number): number {
  const bb = blockBytes(fmt);
  if (bb) return Math.ceil(w / 4) * Math.ceil(h / 4) * bb;
  return w * h * bytesPerPixel(fmt);
}

function decodeImage(fmt: number, b: Uint8Array, off: number, w: number, h: number): Uint8Array | null {
  switch (fmt) {
    case FMT.DXT1:
    case FMT.DXT1_ONEBITALPHA:
      return decodeBc1(b, off, w, h);
    case FMT.DXT3:
      return decodeBc2(b, off, w, h);
    case FMT.DXT5:
      return decodeBc3(b, off, w, h);
    default:
      return decodeUncompressed(fmt, b, off, w, h);
  }
}

// ---- BC1 (DXT1) ----
function decodeBc1(b: Uint8Array, off: number, w: number, h: number): Uint8Array {
  const out = new Uint8Array(w * h * 4);
  const bw = Math.ceil(w / 4);
  const bh = Math.ceil(h / 4);
  for (let by = 0; by < bh; by++) {
    for (let bx = 0; bx < bw; bx++) {
      const p = off + (by * bw + bx) * 8;
      const c0 = b[p] | (b[p + 1] << 8);
      const c1 = b[p + 2] | (b[p + 3] << 8);
      const cols = colorTable(c0, c1);
      for (let y = 0; y < 4; y++) {
        const bits = b[p + 4 + y];
        for (let x = 0; x < 4; x++) {
          const idx = (bits >> (x * 2)) & 3;
          putBlockPixel(out, w, h, bx, by, x, y, cols[idx]);
        }
      }
    }
  }
  return out;
}

// ---- BC2 (DXT3) ----
function decodeBc2(b: Uint8Array, off: number, w: number, h: number): Uint8Array {
  const out = new Uint8Array(w * h * 4);
  const bw = Math.ceil(w / 4);
  const bh = Math.ceil(h / 4);
  for (let by = 0; by < bh; by++) {
    for (let bx = 0; bx < bw; bx++) {
      const p = off + (by * bw + bx) * 16;
      // 8 bytes alpha (4 bits/pixel), then 8 bytes BC1-style color (always 4-color).
      const c0 = b[p + 8] | (b[p + 9] << 8);
      const c1 = b[p + 10] | (b[p + 11] << 8);
      const cols = colorTable4(c0, c1);
      for (let y = 0; y < 4; y++) {
        const alphaRow = b[p + y * 2] | (b[p + y * 2 + 1] << 8);
        const bits = b[p + 12 + y];
        for (let x = 0; x < 4; x++) {
          const idx = (bits >> (x * 2)) & 3;
          const a = ((alphaRow >> (x * 4)) & 0xf) * 17;
          putBlockPixel(out, w, h, bx, by, x, y, cols[idx], a);
        }
      }
    }
  }
  return out;
}

// ---- BC3 (DXT5) ----
function decodeBc3(b: Uint8Array, off: number, w: number, h: number): Uint8Array {
  const out = new Uint8Array(w * h * 4);
  const bw = Math.ceil(w / 4);
  const bh = Math.ceil(h / 4);
  for (let by = 0; by < bh; by++) {
    for (let bx = 0; bx < bw; bx++) {
      const p = off + (by * bw + bx) * 16;
      const a0 = b[p];
      const a1 = b[p + 1];
      // 48-bit alpha index table
      let alphaBits = 0n;
      for (let i = 0; i < 6; i++) alphaBits |= BigInt(b[p + 2 + i]) << BigInt(8 * i);
      const alphas = alphaTable(a0, a1);
      const c0 = b[p + 8] | (b[p + 9] << 8);
      const c1 = b[p + 10] | (b[p + 11] << 8);
      const cols = colorTable4(c0, c1);
      for (let y = 0; y < 4; y++) {
        const bits = b[p + 12 + y];
        for (let x = 0; x < 4; x++) {
          const ci = (bits >> (x * 2)) & 3;
          const ai = Number((alphaBits >> BigInt(3 * (y * 4 + x))) & 7n);
          putBlockPixel(out, w, h, bx, by, x, y, cols[ci], alphas[ai]);
        }
      }
    }
  }
  return out;
}

function r5g6b5(c: number): [number, number, number] {
  const r = ((c >> 11) & 0x1f) * 255 / 31 | 0;
  const g = ((c >> 5) & 0x3f) * 255 / 63 | 0;
  const bl = (c & 0x1f) * 255 / 31 | 0;
  return [r, g, bl];
}

/** BC1 color table (c0<=c1 => 3 colors + transparent). Returns RGBA tuples. */
function colorTable(c0: number, c1: number): [number, number, number, number][] {
  const [r0, g0, b0] = r5g6b5(c0);
  const [r1, g1, b1] = r5g6b5(c1);
  const t: [number, number, number, number][] = [
    [r0, g0, b0, 255],
    [r1, g1, b1, 255],
  ];
  if (c0 > c1) {
    t.push([(2 * r0 + r1) / 3 | 0, (2 * g0 + g1) / 3 | 0, (2 * b0 + b1) / 3 | 0, 255]);
    t.push([(r0 + 2 * r1) / 3 | 0, (g0 + 2 * g1) / 3 | 0, (b0 + 2 * b1) / 3 | 0, 255]);
  } else {
    t.push([(r0 + r1) / 2 | 0, (g0 + g1) / 2 | 0, (b0 + b1) / 2 | 0, 255]);
    t.push([0, 0, 0, 0]);
  }
  return t;
}

/** BC2/BC3 color table (always 4 opaque colors). */
function colorTable4(c0: number, c1: number): [number, number, number, number][] {
  const [r0, g0, b0] = r5g6b5(c0);
  const [r1, g1, b1] = r5g6b5(c1);
  return [
    [r0, g0, b0, 255],
    [r1, g1, b1, 255],
    [(2 * r0 + r1) / 3 | 0, (2 * g0 + g1) / 3 | 0, (2 * b0 + b1) / 3 | 0, 255],
    [(r0 + 2 * r1) / 3 | 0, (g0 + 2 * g1) / 3 | 0, (b0 + 2 * b1) / 3 | 0, 255],
  ];
}

function alphaTable(a0: number, a1: number): number[] {
  const t = [a0, a1];
  if (a0 > a1) {
    for (let i = 1; i <= 6; i++) t.push(Math.round(((7 - i) * a0 + i * a1) / 7));
  } else {
    for (let i = 1; i <= 4; i++) t.push(Math.round(((5 - i) * a0 + i * a1) / 5));
    t.push(0, 255);
  }
  return t;
}

function putBlockPixel(
  out: Uint8Array,
  w: number,
  h: number,
  bx: number,
  by: number,
  x: number,
  y: number,
  c: [number, number, number, number],
  alpha?: number,
) {
  const px = bx * 4 + x;
  const py = by * 4 + y;
  if (px >= w || py >= h) return;
  const o = (py * w + px) * 4;
  out[o] = c[0];
  out[o + 1] = c[1];
  out[o + 2] = c[2];
  out[o + 3] = alpha === undefined ? c[3] : alpha;
}

function decodeUncompressed(fmt: number, b: Uint8Array, off: number, w: number, h: number): Uint8Array | null {
  const out = new Uint8Array(w * h * 4);
  const n = w * h;
  for (let i = 0; i < n; i++) {
    const o = off + i * bytesPerPixel(fmt);
    let r = 255, g = 255, bl = 255, a = 255;
    switch (fmt) {
      case FMT.RGBA8888: r = b[o]; g = b[o + 1]; bl = b[o + 2]; a = b[o + 3]; break;
      case FMT.ABGR8888: a = b[o]; bl = b[o + 1]; g = b[o + 2]; r = b[o + 3]; break;
      case FMT.ARGB8888: a = b[o]; r = b[o + 1]; g = b[o + 2]; bl = b[o + 3]; break;
      case FMT.BGRA8888: bl = b[o]; g = b[o + 1]; r = b[o + 2]; a = b[o + 3]; break;
      case FMT.BGRX8888: bl = b[o]; g = b[o + 1]; r = b[o + 2]; a = 255; break;
      case FMT.UVLX8888: r = b[o]; g = b[o + 1]; bl = b[o + 2]; a = b[o + 3]; break;
      case FMT.RGB888: r = b[o]; g = b[o + 1]; bl = b[o + 2]; break;
      case FMT.BGR888: bl = b[o]; g = b[o + 1]; r = b[o + 2]; break;
      case FMT.RGB565: { const c = b[o] | (b[o + 1] << 8); const [rr, gg, bb] = r5g6b5(c); r = rr; g = gg; bl = bb; break; }
      case FMT.BGR565: { const c = b[o + 1] | (b[o] << 8); const [rr, gg, bb] = r5g6b5(c); r = rr; g = gg; bl = bb; break; }
      case FMT.BGRX5551: case FMT.BGRA5551: {
        const c = b[o] | (b[o + 1] << 8);
        r = ((c >> 10) & 0x1f) * 255 / 31 | 0;
        g = ((c >> 5) & 0x1f) * 255 / 31 | 0;
        bl = (c & 0x1f) * 255 / 31 | 0;
        a = fmt === FMT.BGRA5551 ? ((c >> 15) & 1) * 255 : 255;
        break;
      }
      case FMT.BGRA4444: {
        const c = b[o] | (b[o + 1] << 8);
        a = ((c >> 12) & 0xf) * 17;
        r = ((c >> 8) & 0xf) * 17;
        g = ((c >> 4) & 0xf) * 17;
        bl = (c & 0xf) * 17;
        break;
      }
      case FMT.I8: r = g = bl = b[o]; break;
      case FMT.A8: r = g = bl = 255; a = b[o]; break;
      case FMT.IA88: { const v = b[o]; r = g = bl = v; a = b[o + 1]; break; }
      case FMT.UV88: r = bl = b[o]; g = b[o + 1]; a = 255; break;
      case FMT.P8: return null; // needs a palette
      default: return null;
    }
    const q = i * 4;
    out[q] = r; out[q + 1] = g; out[q + 2] = bl; out[q + 3] = a;
  }
  return out;
}
